import { useCallback, useEffect, useMemo } from 'react'
import { create } from 'zustand'

// #413 — which event's row a toast is pointing at: hover and keyboard focus each keep their own id (not persisted).
interface AlertLinkState {
  hoverId: number | null
  focusId: number | null
  setHover: (id: number | null) => void
  setFocus: (id: number | null) => void
  release: (id: number) => void
}

export const useAlertLinkStore = create<AlertLinkState>((set) => ({
  hoverId: null,
  focusId: null,
  setHover: (id) => set({ hoverId: id }),
  setFocus: (id) => set({ focusId: id }),
  release: (id) => set((s) => ({
    hoverId: s.hoverId === id ? null : s.hoverId,
    focusId: s.focusId === id ? null : s.focusId,
  })),
}))

/** True while a toast of this event is hovered or focused (only the rows whose answer flips re-render). */
export function useAlertLinked(eventId: number): boolean {
  return useAlertLinkStore((s) => s.hoverId === eventId || s.focusId === eventId)
}

/** Handlers for a toast's root: enter/focus point at the row, leave/blur clear it only if it is still this id. */
export function useAlertLinkHandlers(eventId: number | undefined) {
  const setHover = useAlertLinkStore((s) => s.setHover)
  const setFocus = useAlertLinkStore((s) => s.setFocus)
  const release = useAlertLinkStore((s) => s.release)
  useEffect(() => {
    if (typeof eventId !== 'number') return undefined
    return () => release(eventId)
  }, [eventId, release])
  const onMouseEnter = useCallback(() => { if (typeof eventId === 'number') setHover(eventId) }, [eventId, setHover])
  const onMouseLeave = useCallback(() => {
    if (typeof eventId === 'number' && useAlertLinkStore.getState().hoverId === eventId) setHover(null)
  }, [eventId, setHover])
  const onFocus = useCallback(() => { if (typeof eventId === 'number') setFocus(eventId) }, [eventId, setFocus])
  const onBlur = useCallback(() => {
    if (typeof eventId === 'number' && useAlertLinkStore.getState().focusId === eventId) setFocus(null)
  }, [eventId, setFocus])
  return useMemo(() => ({ onMouseEnter, onMouseLeave, onFocus, onBlur }), [onMouseEnter, onMouseLeave, onFocus, onBlur])
}
